import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EngagementTeamPanel from './EngagementTeamPanel.jsx';
import Tabs from '../components/shared/Tabs.jsx';
import {
  listRequests,
  exportAuditor,
  exportZip,
  readTenantArtifacts,
  requestTenantAccess,
  readNotes,
  writeNote,
  updateEngagement,
  findTenantByEmailDomain,
  findTenantByName,
  isGenericDomain,
  inviteTenant,
} from './grcClient.js';

const REQUEST_STATUS = {
  pending: { color: 'var(--fg3)', label: 'Pending' },
  'info-requested': { color: 'var(--warn)', label: 'Info Requested' },
  uploaded: { color: 'var(--accent)', label: 'Uploaded' },
  'under-review': { color: 'var(--fg2)', label: 'Under Review' },
  accepted: { color: '#4ade80', label: 'Accepted' },
  'not-applicable': { color: 'var(--fg3)', label: 'N/A' },
  returned: { color: 'var(--danger)', label: 'Returned' },
  concluded: { color: '#4ade80', label: 'Concluded' },
};

function StatusDot({ status }) {
  const s = REQUEST_STATUS[status] || REQUEST_STATUS.pending;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        color: s.color,
        fontFamily: 'var(--font-mono)',
        fontWeight: 600,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: s.color,
          display: 'inline-block',
        }}
      />
      {s.label}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function ChatThread({ requestId, engagementId }) {
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    readNotes(requestId)
      .then((d) => setNotes(d.notes || []))
      .catch((e) => setError(e.message || 'Failed to load messages'))
      .finally(() => setLoading(false));
  }, [requestId]);

  useEffect(() => {
    if (notes?.length) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [notes]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await writeNote({ requestId, engagementId, content: text });
      setNotes((n) => [...(n || []), res.note]);
      setDraft('');
    } catch (e) {
      setError(e.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        margin: '12px 16px 0',
        borderRadius: 8,
        border: '1px solid var(--border)',
        background: 'var(--bg)',
        overflow: 'hidden',
      }}
    >
      {/* Thread header */}
      <div
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--border)',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--fg3)',
          fontFamily: 'var(--font-mono)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          background: 'var(--bg2)',
        }}
      >
        Client conversation
      </div>

      {/* Messages */}
      <div style={{ maxHeight: 260, overflowY: 'auto', padding: '10px 12px' }}>
        {loading && (
          <div
            style={{ color: 'var(--fg3)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}
          >
            Loading…
          </div>
        )}
        {!loading && notes?.length === 0 && (
          <div
            style={{ color: 'var(--fg3)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}
          >
            No messages yet. Start a conversation with your client below.
          </div>
        )}
        {notes?.map((note) => {
          const isMe = note.authorType === 'auditor';
          return (
            <div
              key={note.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMe ? 'flex-end' : 'flex-start',
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  maxWidth: '80%',
                  padding: '7px 11px',
                  borderRadius: 10,
                  fontSize: 13,
                  lineHeight: 1.5,
                  background: isMe ? 'var(--accent-bg)' : 'var(--bg3)',
                  color: 'var(--fg)',
                  borderBottomRightRadius: isMe ? 2 : 10,
                  borderBottomLeftRadius: isMe ? 10 : 2,
                  border: '1px solid var(--border)',
                }}
              >
                {note.content}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--fg3)',
                  marginTop: 3,
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {note.authorName} · {fmtTime(note.createdAt)}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '6px 12px',
            fontSize: 12,
            color: 'var(--danger)',
            background: 'var(--bg2)',
          }}
        >
          {error}
        </div>
      )}

      {/* Compose */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '8px 12px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg2)',
        }}
      >
        <input
          className="input"
          style={{ flex: 1, fontSize: 12, padding: '6px 10px', height: 34 }}
          placeholder="Message to client…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={sending}
        />
        <button
          className="btn btn-sm btn-primary"
          onClick={send}
          disabled={sending || !draft.trim()}
          style={{ flexShrink: 0 }}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

function RequestRow({ req, engagementId }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          cursor: 'pointer',
          background: expanded ? 'var(--bg2)' : 'transparent',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--fg)', fontWeight: req.isRequired ? 600 : 400 }}>
            {req.title}
            {req.isRequired && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 9,
                  color: 'var(--danger)',
                  fontFamily: 'var(--font-mono)',
                  verticalAlign: 'middle',
                }}
              >
                REQUIRED
              </span>
            )}
          </div>
          {req.description && (
            <div style={{ fontSize: 12, color: 'var(--fg3)', marginTop: 3, lineHeight: 1.5 }}>
              {req.description}
            </div>
          )}
          {req.notApplicableReason && (
            <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 4, fontStyle: 'italic' }}>
              N/A: {req.notApplicableReason}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
          {req.uploadCount > 0 && (
            <span style={{ fontSize: 11, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}>
              {req.uploadCount} file{req.uploadCount !== 1 ? 's' : ''}
            </span>
          )}
          <StatusDot status={req.status} />
          <span
            style={{
              color: 'var(--fg3)',
              fontSize: 12,
              transform: expanded ? 'rotate(90deg)' : '',
              transition: 'transform 0.15s',
            }}
          >
            ›
          </span>
        </div>
      </div>
      {expanded && (
        <div style={{ paddingBottom: 14 }}>
          <ChatThread requestId={req.id} engagementId={engagementId} />
        </div>
      )}
    </div>
  );
}

function SectionPanel({ section, requests, engagementId }) {
  const total = requests.length;
  const accepted = requests.filter(
    (r) => r.status === 'accepted' || r.status === 'concluded'
  ).length;
  const naCount = requests.filter((r) => r.status === 'not-applicable').length;
  const done = accepted + naCount;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      {/* Section header */}
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'var(--bg2)',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--fg)' }}>{section.title}</div>
        <div style={{ fontSize: 11, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}>
          {done}/{total} complete
        </div>
      </div>

      {/* Requests */}
      {requests.length === 0 ? (
        <div
          style={{ padding: '20px 16px', color: 'var(--fg3)', fontSize: 12, textAlign: 'center' }}
        >
          No requests in this section
        </div>
      ) : (
        requests.map((req) => <RequestRow key={req.id} req={req} engagementId={engagementId} />)
      )}
    </div>
  );
}

// ── Linked Tryggr workspace artifacts panel ──────────────────────────────────

function ArtifactBadge({ count, label }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '10px 16px',
        borderRadius: 8,
        background: 'var(--bg3)',
        border: '1px solid var(--border)',
        minWidth: 80,
      }}
    >
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: 'var(--accent)',
          fontFamily: 'var(--font-display)',
        }}
      >
        {count}
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function ArtifactRow({ item, kind }) {
  const statusColor = {
    published: '#4ade80',
    in_review: 'var(--accent)',
    to_review: 'var(--warn)',
    verified: '#4ade80',
    expired: 'var(--danger)',
    active: 'var(--accent)',
    open: 'var(--warn)',
    'in-analysis': 'var(--accent)',
    'pending-approval': 'var(--warn)',
    approved: '#4ade80',
    resolved: 'var(--fg3)',
  };
  const color = statusColor[item.status] || 'var(--fg3)';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 14px',
        borderBottom: '1px solid var(--border)',
        fontSize: 13,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          flex: 1,
          color: 'var(--fg)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {item.title}
      </span>
      {kind === 'document' && item.type && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--fg3)',
            fontFamily: 'var(--font-mono)',
            flexShrink: 0,
          }}
        >
          {item.type}
        </span>
      )}
      {kind === 'risk' && item.residualScore != null && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--fg3)',
            fontFamily: 'var(--font-mono)',
            flexShrink: 0,
          }}
        >
          Score {item.residualScore}
        </span>
      )}
      {kind === 'evidence' && item.validUntil && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--fg3)',
            fontFamily: 'var(--font-mono)',
            flexShrink: 0,
          }}
        >
          Valid until {new Date(item.validUntil).toLocaleDateString()}
        </span>
      )}
      <span
        style={{
          fontSize: 11,
          color,
          fontFamily: 'var(--font-mono)',
          flexShrink: 0,
          textTransform: 'uppercase',
        }}
      >
        {item.status}
      </span>
    </div>
  );
}

function LinkedArtifactsPanel({ engagementId }) {
  const [artifacts, setArtifacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [activeTab, setActiveTab] = useState('documents');

  useEffect(() => {
    readTenantArtifacts(engagementId)
      .then((data) => setArtifacts(data))
      .catch((e) => setError(e.message || 'Failed to load artifacts'))
      .finally(() => setLoading(false));
  }, [engagementId]);

  const handleRequestAccess = async () => {
    setRequesting(true);
    try {
      await requestTenantAccess({ engagementId });
      setRequestSent(true);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to send access request');
    } finally {
      setRequesting(false);
    }
  };

  const containerStyle = {
    marginTop: 32,
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    overflow: 'hidden',
  };

  const headerStyle = {
    padding: '14px 18px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--bg2)',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg)' }}>
            Linked Tryggr workspace
          </div>
        </div>
        <div style={{ padding: '20px 18px', color: 'var(--fg3)', fontSize: 13 }}>Loading…</div>
      </div>
    );
  }

  // Access pending / not yet granted — show the request state.
  if (error) {
    const isPending = error.includes("'pending'");
    const isDeclined = error.includes("'declined'");
    const isRevoked = error.includes("'revoked'");
    const isNoRequest = error.includes('No access request');

    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg)' }}>
            Linked Tryggr workspace
          </div>
        </div>
        <div style={{ padding: '20px 18px' }}>
          {isPending && (
            <p style={{ fontSize: 13, color: 'var(--fg3)', margin: 0 }}>
              Access request is <strong style={{ color: 'var(--warn)' }}>pending</strong> — waiting
              for the client to accept in their workspace.
            </p>
          )}
          {(isDeclined || isRevoked) && (
            <>
              <p style={{ fontSize: 13, color: 'var(--fg3)', margin: '0 0 12px' }}>
                The client has{' '}
                <strong style={{ color: 'var(--danger)' }}>
                  {isRevoked ? 'revoked' : 'declined'}
                </strong>{' '}
                access. You can send a new request.
              </p>
              {requestSent ? (
                <p style={{ fontSize: 13, color: '#4ade80', margin: 0 }}>
                  ✓ New access request sent.
                </p>
              ) : (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleRequestAccess}
                  disabled={requesting}
                >
                  {requesting ? 'Sending…' : 'Send new access request'}
                </button>
              )}
            </>
          )}
          {isNoRequest && (
            <>
              <p style={{ fontSize: 13, color: 'var(--fg3)', margin: '0 0 12px' }}>
                No access request has been sent yet.
              </p>
              {requestSent ? (
                <p style={{ fontSize: 13, color: '#4ade80', margin: 0 }}>
                  ✓ Access request sent. Waiting for client confirmation.
                </p>
              ) : (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleRequestAccess}
                  disabled={requesting}
                >
                  {requesting ? 'Sending…' : 'Request access to workspace artifacts'}
                </button>
              )}
            </>
          )}
          {!isPending && !isDeclined && !isRevoked && !isNoRequest && (
            <p style={{ fontSize: 13, color: 'var(--fg3)', margin: 0 }}>{error}</p>
          )}
        </div>
      </div>
    );
  }

  if (!artifacts) return null;

  const tabs = [
    { id: 'documents', label: `Documents (${artifacts.counts.documents})` },
    { id: 'evidence', label: `Evidence (${artifacts.counts.evidence})` },
    { id: 'risks', label: `Risks (${artifacts.counts.risks})` },
  ];

  const tabItems = {
    documents: artifacts.documents || [],
    evidence: artifacts.evidence || [],
    risks: artifacts.risks || [],
  };

  const kindMap = { documents: 'document', evidence: 'evidence', risks: 'risk' };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg)' }}>
            Linked Tryggr workspace
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg3)',
              marginTop: 2,
              fontFamily: 'var(--font-mono)',
            }}
          >
            {artifacts.tryggrSlug} · Read-only · Access granted
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <ArtifactBadge count={artifacts.counts.documents} label="Documents" />
          <ArtifactBadge count={artifacts.counts.evidence} label="Evidence" />
          <ArtifactBadge count={artifacts.counts.risks} label="Risks" />
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        ariaLabel="Engagement sections"
        tabs={tabs}
        active={activeTab}
        onChange={setActiveTab}
        style={{
          marginBottom: 0,
          background: 'var(--bg2)',
        }}
      />

      {/* Artifact list */}
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {tabItems[activeTab].length === 0 ? (
          <div
            style={{ padding: '20px 18px', color: 'var(--fg3)', fontSize: 13, textAlign: 'center' }}
          >
            No {activeTab} found
          </div>
        ) : (
          tabItems[activeTab].map((item) => (
            <ArtifactRow key={item.id} item={item} kind={kindMap[activeTab]} />
          ))
        )}
      </div>
    </div>
  );
}

// ── Main engagement detail ────────────────────────────────────────────────────

export default function GrcEngagementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportUrl, setExportUrl] = useState('');
  const [exportingZip, setExportingZip] = useState(false);
  const [exportZipUrl, setExportZipUrl] = useState('');
  const [engagement, setEngagement] = useState(null);

  // CVR edit state
  const [editingCvr, setEditingCvr] = useState(false);
  const [cvrEditValue, setCvrEditValue] = useState('');
  const [cvrEditSaving, setCvrEditSaving] = useState(false);

  // Rescan state
  const [rescanning, setRescanning] = useState(false);
  const [rescanMsg, setRescanMsg] = useState('');

  // Invite state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');

  const handleSaveCvr = async () => {
    setCvrEditSaving(true);
    try {
      await updateEngagement(id, { cvrNumber: cvrEditValue.trim() || null });
      setEngagement((prev) => ({ ...prev, cvrNumber: cvrEditValue.trim() || null }));
      setEditingCvr(false);
    } catch (e) {
      alert(e.message || 'Failed to save CVR');
    } finally {
      setCvrEditSaving(false);
    }
  };

  const handleRescan = async () => {
    if (!engagement) return;
    setRescanning(true);
    setRescanMsg('');
    try {
      let found = null;
      if (engagement.contactEmail && !isGenericDomain(engagement.contactEmail)) {
        found = await findTenantByEmailDomain(engagement.contactEmail);
      }
      if (!found && engagement.clientName) {
        found = await findTenantByName(engagement.clientName);
      }
      if (found) {
        await updateEngagement(id, { tryggrSlug: found.slug });
        setEngagement((prev) => ({ ...prev, tryggrSlug: found.slug }));
        setRescanMsg(`Linked to workspace: ${found.slug}`);
      } else {
        setRescanMsg('No Tryggr workspace found for this company.');
      }
    } catch (e) {
      setRescanMsg('Rescan failed — please try again.');
    } finally {
      setRescanning(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteMsg('');
    try {
      const result = await inviteTenant(id, {
        adminEmail: inviteEmail.trim(),
        firstName: inviteFirstName.trim() || undefined,
        lastName: inviteLastName.trim() || undefined,
      });
      setEngagement((prev) => ({
        ...prev,
        tryggrSlug: result.slug,
        inviteSentAt: result.inviteSentAt,
      }));
      setInviteMsg(`Invite sent — workspace: ${result.slug}`);
      setShowInviteForm(false);
    } catch (e) {
      setInviteMsg(e.message || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  useEffect(() => {
    listRequests(id)
      .then((r) => {
        setSections(r.sections || []);
        setRequests(r.requests || []);
        setEngagement(r.engagement || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const handleExport = async () => {
    setExporting(true);
    setExportUrl('');
    try {
      const r = await exportAuditor(id);
      setExportUrl(r.downloadUrl);
    } catch (e) {
      alert(e.body?.error || e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportZip = async () => {
    setExportingZip(true);
    setExportZipUrl('');
    try {
      const r = await exportZip(id);
      setExportZipUrl(r.downloadUrl);
    } catch (e) {
      alert(e.body?.error || e.message || 'ZIP export failed');
    } finally {
      setExportingZip(false);
    }
  };

  const requestsBySection = {};
  for (const req of requests) {
    const sid = req.sectionId || '__none__';
    if (!requestsBySection[sid]) requestsBySection[sid] = [];
    requestsBySection[sid].push(req);
  }

  const totalRequests = requests.length;
  const acceptedCount = requests.filter(
    (r) => r.status === 'accepted' || r.status === 'concluded'
  ).length;
  const pendingCount = requests.filter((r) => r.status === 'pending').length;
  const uploadedCount = requests.filter(
    (r) => r.status === 'uploaded' || r.status === 'under-review'
  ).length;

  return (
    <div style={{ padding: '32px 40px', maxWidth: 860, margin: '0 auto' }}>
      {/* Back */}
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => navigate('/')}
        style={{ marginBottom: 24 }}
      >
        ← Back to engagements
      </button>

      {loading && <div style={{ color: 'var(--fg3)', fontSize: 13 }}>Loading…</div>}
      {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

      {!loading && !error && (
        <>
          {/* Stats bar */}
          <div
            style={{
              display: 'flex',
              gap: 24,
              marginBottom: 32,
              padding: '16px 20px',
              borderRadius: 12,
              background: 'var(--bg2)',
              border: '1px solid var(--border)',
            }}
          >
            {[
              { label: 'Total requests', value: totalRequests, color: 'var(--fg)' },
              { label: 'Accepted', value: acceptedCount, color: '#4ade80' },
              { label: 'Uploaded', value: uploadedCount, color: 'var(--accent)' },
              { label: 'Pending', value: pendingCount, color: 'var(--fg3)' },
            ].map((s) => (
              <div key={s.label} style={{ textAlign: 'center', flex: 1 }}>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: s.color,
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  {s.value}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
              {/* ZIP export — files + conversations */}
              {exportZipUrl ? (
                <a
                  href={exportZipUrl}
                  download
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    background: 'var(--accent)',
                    color: 'var(--bg)',
                    textDecoration: 'none',
                  }}
                >
                  ↓ Download ZIP
                </a>
              ) : (
                <button
                  onClick={handleExportZip}
                  disabled={exportingZip}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: 'var(--bg3)',
                    color: 'var(--fg)',
                    cursor: exportingZip ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    opacity: exportingZip ? 0.6 : 1,
                  }}
                >
                  {exportingZip ? 'Building ZIP…' : 'Export (.zip)'}
                </button>
              )}
              {/* Excel export */}
              {exportUrl ? (
                <a
                  href={exportUrl}
                  download
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    background: '#4ade80',
                    color: '#052e16',
                    textDecoration: 'none',
                  }}
                >
                  ↓ Download export
                </a>
              ) : (
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: 'var(--bg3)',
                    color: 'var(--fg)',
                    cursor: exporting ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    opacity: exporting ? 0.6 : 1,
                  }}
                >
                  {exporting ? 'Generating…' : 'Export (.xlsx)'}
                </button>
              )}
            </div>
          </div>

          {/* CVR / Tryggr workspace panel */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 24,
              padding: '12px 16px',
              borderRadius: 10,
              background: 'var(--bg2)',
              border: '1px solid var(--border)',
              marginBottom: 16,
              flexWrap: 'wrap',
            }}
          >
            {/* CVR field */}
            <div style={{ flex: '1 1 200px' }}>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--fg3)',
                  marginBottom: 4,
                  fontFamily: 'var(--font-mono)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                CVR / Company ID
              </div>
              {editingCvr ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    className="input"
                    style={{ fontSize: 12, padding: '4px 8px', width: 130 }}
                    value={cvrEditValue}
                    onChange={(e) => setCvrEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveCvr();
                      if (e.key === 'Escape') setEditingCvr(false);
                    }}
                    autoFocus
                  />
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleSaveCvr}
                    disabled={cvrEditSaving}
                    style={{ fontSize: 11, padding: '3px 10px' }}
                  >
                    {cvrEditSaving ? '…' : 'Save'}
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => setEditingCvr(false)}
                    style={{ fontSize: 11, padding: '3px 8px' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      fontSize: 13,
                      color: engagement?.cvrNumber ? 'var(--fg)' : 'var(--fg3)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {engagement?.cvrNumber || '—'}
                  </span>
                  <button
                    className="btn btn-sm btn-ghost"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                    onClick={() => {
                      setCvrEditValue(engagement?.cvrNumber || '');
                      setEditingCvr(true);
                    }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>

            {/* Tryggr workspace field */}
            <div style={{ flex: '1 1 200px' }}>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--fg3)',
                  marginBottom: 4,
                  fontFamily: 'var(--font-mono)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Tryggr workspace
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span
                  style={{
                    fontSize: 13,
                    color: engagement?.tryggrSlug ? 'var(--accent)' : 'var(--fg3)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {engagement?.tryggrSlug || 'Not linked'}
                </span>
                {engagement?.tryggrSlug && (
                  <button
                    className="btn btn-sm btn-ghost"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                    onClick={async () => {
                      if (!confirm('Remove the Tryggr workspace link from this engagement?'))
                        return;
                      try {
                        await updateEngagement(id, { tryggrSlug: null });
                        setEngagement((prev) => ({ ...prev, tryggrSlug: null }));
                        setRescanMsg('');
                      } catch (e) {
                        alert(e.message || 'Failed to unlink');
                      }
                    }}
                  >
                    Unlink
                  </button>
                )}
                <button
                  className="btn btn-sm btn-ghost"
                  style={{ fontSize: 11, padding: '2px 10px' }}
                  onClick={handleRescan}
                  disabled={rescanning}
                >
                  {rescanning ? 'Scanning…' : '↻ Rescan'}
                </button>
                {!engagement?.tryggrSlug && (
                  <button
                    className="btn btn-sm btn-primary"
                    style={{ fontSize: 11, padding: '2px 10px' }}
                    onClick={() => {
                      setInviteEmail(engagement?.contactEmail || '');
                      setInviteFirstName('');
                      setInviteLastName('');
                      setInviteMsg('');
                      setShowInviteForm(true);
                    }}
                  >
                    Send invite
                  </button>
                )}
              </div>

              {/* Invite sent timestamp */}
              {engagement?.inviteSentAt && (
                <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 4 }}>
                  Invite sent{' '}
                  {new Date(engagement.inviteSentAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </div>
              )}

              {rescanMsg && (
                <div
                  style={{
                    fontSize: 11,
                    color:
                      rescanMsg.startsWith('Linked') || rescanMsg.startsWith('Invite')
                        ? 'var(--accent)'
                        : 'var(--fg3)',
                    marginTop: 4,
                  }}
                >
                  {rescanMsg}
                </div>
              )}

              {/* Inline invite form */}
              {showInviteForm && (
                <div
                  style={{
                    marginTop: 10,
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: 'var(--bg3)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div style={{ fontSize: 11, color: 'var(--fg2)', marginBottom: 8 }}>
                    A new free Tryggr workspace will be created and a welcome email sent to the
                    client.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input
                      className="input"
                      style={{ fontSize: 12, padding: '4px 8px' }}
                      placeholder="Client email *"
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <input
                        className="input"
                        style={{ fontSize: 12, padding: '4px 8px' }}
                        placeholder="First name"
                        value={inviteFirstName}
                        onChange={(e) => setInviteFirstName(e.target.value)}
                      />
                      <input
                        className="input"
                        style={{ fontSize: 12, padding: '4px 8px' }}
                        placeholder="Last name"
                        value={inviteLastName}
                        onChange={(e) => setInviteLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button
                      className="btn btn-sm btn-primary"
                      style={{ fontSize: 11 }}
                      onClick={handleSendInvite}
                      disabled={inviting || !inviteEmail.trim()}
                    >
                      {inviting ? 'Sending…' : 'Send invite'}
                    </button>
                    <button
                      className="btn btn-sm btn-ghost"
                      style={{ fontSize: 11 }}
                      onClick={() => setShowInviteForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                  {inviteMsg && (
                    <div
                      style={{
                        fontSize: 11,
                        color: inviteMsg.startsWith('Invite') ? 'var(--accent)' : 'var(--danger)',
                        marginTop: 6,
                      }}
                    >
                      {inviteMsg}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Who from the firm can reach this engagement (GACA-04) */}
          <div className="card" style={{ padding: '16px 18px', marginBottom: 16 }}>
            <EngagementTeamPanel engagementId={id} />
          </div>

          {/* Linked Tryggr workspace artifacts — shown when engagement has tryggr_slug */}
          {engagement?.tryggrSlug && <LinkedArtifactsPanel engagementId={id} />}

          {/* Sections */}
          {sections.length === 0 && requests.length === 0 ? (
            <div
              style={{ textAlign: 'center', padding: '60px 0', color: 'var(--fg3)', fontSize: 14 }}
            >
              No requests yet. Activate the engagement to copy template requests.
            </div>
          ) : (
            sections.map((sec) => (
              <SectionPanel
                key={sec.id}
                section={sec}
                requests={requestsBySection[sec.id] || []}
                engagementId={id}
              />
            ))
          )}

          {/* Requests with no section */}
          {requestsBySection['__none__']?.length > 0 && (
            <SectionPanel
              section={{ id: '__none__', title: 'Unsectioned' }}
              requests={requestsBySection['__none__']}
              engagementId={id}
            />
          )}
        </>
      )}
    </div>
  );
}
